import { Calendar, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import type { ApiFn } from "@/shared/lib/types";
import { Card, CardContent, CardDescription, CardTitle } from "@/shared/ui/card";

interface AuditEvent {
  event: string;
  user_id: string;
  actor: string;
  actor_id: string | null;
  metadata: Record<string, unknown>;
  ip: string | null;
  user_agent: string | null;
  occurred_at: string;
}

interface AuditTrailResponse {
  items: AuditEvent[];
  total: number;
  skip: number;
  limit: number;
}

interface AuditTrailProps {
  api: ApiFn;
  className?: string;
}

const EVENT_LABELS: Record<string, string> = {
  login: "Login",
  logout: "Logout",
  provider_linked: "Provider Linked",
  provider_unlinked: "Provider Unlinked",
  plan_changed: "Plan Changed",
  offer_redeemed: "Offer Redeemed",
  account_deleted: "Account Deleted",
  admin_action: "Admin Action",
  trial_extended: "Trial Extended",
  phone_verified: "Phone Verified",
};

function formatEventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function parseDeviceName(userAgent: string | null): string {
  if (!userAgent) return "Unknown";
  const ua = userAgent.toLowerCase();
  let os = "Unknown";
  let browser = "Unknown";
  if (/windows nt 10/.test(ua)) os = "Windows 10";
  else if (/windows nt 6\.3/.test(ua)) os = "Windows 8.1";
  else if (/windows nt 6\.1/.test(ua)) os = "Windows 7";
  else if (/windows/.test(ua)) os = "Windows";
  else if (/mac os x/.test(ua)) os = "macOS";
  else if (/iphone/.test(ua)) os = "iPhone";
  else if (/ipad/.test(ua)) os = "iPad";
  else if (/android/.test(ua)) {
    const match = ua.match(/android\s([\d.]+)/);
    os = match ? `Android ${match[1]}` : "Android";
  }
  else if (/linux/.test(ua)) os = "Linux";
  if (/edg\//.test(ua)) browser = "Edge";
  else if (/opr\/|opera/.test(ua)) browser = "Opera";
  else if (/chrome\//.test(ua)) browser = "Chrome";
  else if (/firefox\//.test(ua)) browser = "Firefox";
  else if (/safari\//.test(ua)) browser = "Safari";
  return `${browser} on ${os}`;
}

function formatMetadata(meta: Record<string, unknown>): string {
  const entries = Object.entries(meta);
  if (entries.length === 0) return "—";
  return entries
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(", ");
}

export default function AuditTrail({ api, className }: AuditTrailProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const limit = 20;

  const fetchPage = useCallback(
    async (offset: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api<AuditTrailResponse>(
          "GET",
          `/api/user/me/audit-trail?skip=${offset}&limit=${limit}`,
        );
        if (offset === 0) {
          setEvents(res.items);
        } else {
          setEvents((prev) => [...prev, ...res.items]);
        }
        setTotal(res.total);
        setSkip(offset);
        setLoaded(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load audit trail");
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  const handleToggle = () => {
    if (collapsed && !loaded && !loading) {
      fetchPage(0);
    }
    setCollapsed((c) => !c);
  };

  const hasMore = events.length < total;

  return (
    <Card className={`border-border/60 shadow-sm py-0 gap-0 ${className ?? ""}`}>
      <button
        type="button"
        onClick={handleToggle}
        className={`flex w-full items-center justify-between gap-3 px-5 pt-5 text-left ${collapsed ? "pb-5" : "pb-0"}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Calendar size={16} />
          </div>
          <div>
            <CardTitle className="text-base">Login Activity</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Security audit trail of your account access events
            </CardDescription>
          </div>
        </div>
        <ChevronDown
          size={18}
          className={`shrink-0 text-muted-foreground transition-transform duration-200 ${collapsed ? "" : "rotate-180"}`}
        />
      </button>
      {!collapsed && (
      <CardContent className="px-5 pb-5">
        {!loaded && !loading && (
          <p className="text-sm text-muted-foreground py-4">
            Click "Load more" to fetch activity.
          </p>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 size={14} className="animate-spin" />
            Loading…
          </div>
        )}

        {error && (
          <p className="text-sm text-loss py-2">{error}</p>
        )}

        {loaded && events.length === 0 && !error && (
          <p className="text-sm text-muted-foreground py-4">No activity recorded yet.</p>
        )}

        {events.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/40 text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-3 font-medium">Event</th>
                    <th className="pb-2 pr-3 font-medium">Date / Time</th>
                    <th className="pb-2 pr-3 font-medium">Device</th>
                    <th className="pb-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((evt, i) => (
                    <tr key={`${evt.occurred_at}-${i}`} className="border-b border-border/20 last:border-0">
                      <td className="py-2.5 pr-3 font-medium text-foreground whitespace-nowrap">
                        {formatEventLabel(evt.event)}
                      </td>
                      <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(evt.occurred_at)}
                      </td>
                      <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">
                        {parseDeviceName(evt.user_agent)}
                      </td>
                      <td className="py-2.5 text-muted-foreground text-xs">
                        {formatMetadata(evt.metadata)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <button
                type="button"
                onClick={() => fetchPage(skip + limit)}
                disabled={loading}
                className="mt-3 flex items-center gap-1 text-sm text-primary hover:underline disabled:opacity-50"
              >
                {loading ? "Loading…" : "Load more"}
                {!loading && <ChevronRight size={14} />}
              </button>
            )}

            <p className="mt-3 text-xs text-muted-foreground">
              Showing {events.length} of {total} events
            </p>
          </>
        )}
      </CardContent>
      )}
    </Card>
  );
}
